import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { API_CONFIG, getTenantDomain } from '../../utils/constants';

// Map service names to Bootstrap Icons
const SERVICE_ICON_MAP = {
  opd: 'bi-clipboard2-pulse',
  consultation: 'bi-clipboard2-pulse',
  lab: 'bi-droplet-fill',
  pathology: 'bi-eyedropper',
  test: 'bi-droplet-fill',
  xray: 'bi-camera-fill',
  radiology: 'bi-camera-fill',
  imaging: 'bi-camera-fill',
  pharmacy: 'bi-capsule',
  medicine: 'bi-capsule',
  medical: 'bi-capsule',
  surgery: 'bi-scissors',
  operation: 'bi-scissors',
  dental: 'bi-emoji-smile',
  eye: 'bi-eye',
  vision: 'bi-eye',
  cardio: 'bi-heart-pulse-fill',
  heart: 'bi-heart-pulse-fill',
  emergency: 'bi-truck',
  ambulance: 'bi-truck',
  pediatric: 'bi-people-fill',
  child: 'bi-people-fill',
  physio: 'bi-person-walking',
  therapy: 'bi-person-walking',
  skin: 'bi-stars',
  derma: 'bi-stars',
  ent: 'bi-ear-fill',
  ortho: 'bi-bandaid-fill',
  bone: 'bi-bandaid-fill',
};

const getServiceIcon = (name) => {
  const lower = (name || '').toLowerCase();
  for (const [key, icon] of Object.entries(SERVICE_ICON_MAP)) {
    if (lower.includes(key)) return icon;
  }
  return 'bi-plus-circle-fill';
};

const getInitials = (name) =>
  (name || 'D')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const hexToRgb = (hex) => {
  const h = (hex || '#0d6efd').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) || 13;
  const g = parseInt(h.substring(2, 4), 16) || 110;
  const b = parseInt(h.substring(4, 6), 16) || 253;
  return `${r},${g},${b}`;
};

const EMERGENCY_SERVICES = [
  { key: 'ambulance_number', label: 'Ambulance', icon: 'bi-truck', color: '#dc3545', bg: 'rgba(220,53,69,0.1)' },
  {
    key: 'blood_bank_number',
    label: 'Blood Bank',
    icon: 'bi-droplet-half',
    color: '#e83e8c',
    bg: 'rgba(232,62,140,0.1)',
  },
  { key: 'medical_number', label: 'Pharmacy', icon: 'bi-capsule', color: '#0d6efd', bg: 'rgba(13,110,253,0.1)' },
  { key: 'pathology_number', label: 'Pathology', icon: 'bi-eyedropper', color: '#6f42c1', bg: 'rgba(111,66,193,0.1)' },
  { key: 'xray_number', label: 'X-Ray', icon: 'bi-camera-fill', color: '#fd7e14', bg: 'rgba(253,126,20,0.1)' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { logoUrl, isDark, toggleTheme } = useTheme();
  const [branding, setBranding] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [diagnosticsList, setDiagnosticsList] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const treatmentsRef = useRef(null);
  const diagnosticsRef = useRef(null);
  const doctorsRef = useRef(null);
  const galleryRef = useRef(null);
  const testimonialsRef = useRef(null);
  const emergencyRef = useRef(null);
  const contactRef = useRef(null);

  const primaryColor = branding.primary_color || '#0d6efd';
  const primaryRgb = hexToRgb(primaryColor);
  const hospitalName = branding.hospital_name || 'Hospital';

  // Fetch all public data
  useEffect(() => {
    const tenantDomain = getTenantDomain();
    const params = tenantDomain ? `?domain=${tenantDomain}` : '';
    const headers = tenantDomain ? { 'X-Tenant-Domain': tenantDomain } : {};
    const opts = { headers };

    fetch(`${API_CONFIG.BASE_URL}/api/branding${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setBranding(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/doctors${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setDoctors(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/services${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setServices(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/stats${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setStats(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/team-members${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setTeamMembers(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/treatments${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setTreatments(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/diagnostics${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setDiagnosticsList(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/gallery${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setGalleryItems(d.data);
      })
      .catch(() => {});

    fetch(`${API_CONFIG.BASE_URL}/api/public/testimonials${params}`, opts)
      .then((r) => r.json())
      .then((d) => {
        if (d.status && d.data) setTestimonials(d.data);
      })
      .catch(() => {});
  }, []);

  // Scroll listener for navbar
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Intersection Observer for section animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const sections = document.querySelectorAll('.lp-section');
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [doctors, services, teamMembers, treatments, diagnosticsList, galleryItems, testimonials]); // re-observe when data loads

  const scrollTo = useCallback((ref) => {
    setMobileMenuOpen(false);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Generic: photo already contains relative path (e.g. uploads/team/..., uploads/testimonials/...)
  const resolvePhoto = (photo) => {
    if (!photo) return null;
    return `${API_CONFIG.BASE_URL}/${photo}`;
  };
  // Doctors: photo is just filename, stored in uploads/doctors/
  const resolveDoctorPhoto = (photo) => {
    if (!photo) return null;
    return `${API_CONFIG.BASE_URL}/uploads/doctors/${photo}`;
  };

  const emergencyNumbers = EMERGENCY_SERVICES.filter((s) => branding[s.key]);

  const extractMapSrc = (val) => {
    if (!val) return null;

    // 1. Paste of full iframe embed code — extract src attribute
    const iframeSrc = val.match(/src=["']([^"']+)["']/);
    if (iframeSrc) return iframeSrc[1];

    // 2. Already a proper embed URL
    if (val.includes('/maps/embed')) return val;

    // 3. Full Google Maps place URL — extract @lat,lng for reliable embed
    if (val.includes('google.com/maps') || val.includes('google.co.in/maps') || val.includes('maps.google.com')) {
      const coords = val.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coords) {
        return `https://maps.google.com/maps?q=${coords[1]},${coords[2]}&output=embed&z=15`;
      }
      // No coordinates — extract place name from path as fallback
      const place = val.match(/\/place\/([^/@?&]+)/);
      if (place) {
        return `https://maps.google.com/maps?q=${place[1].replace(/\+/g, ' ')}&output=embed`;
      }
    }

    // 4. Short share links (maps.app.goo.gl / goo.gl/maps) — can't be embedded,
    //    return null so the "Open in Google Maps" button is shown instead
    if (val.includes('maps.app.goo.gl') || val.includes('goo.gl/maps')) {
      return null;
    }

    // 5. Plain address or coordinates string
    if (!val.startsWith('http')) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(val)}&output=embed`;
    }

    return null;
  };

  return (
    <div className="lp-wrapper" style={{ '--lp-primary': primaryColor, '--lp-primary-rgb': primaryRgb }}>
      {/* ===== NAVBAR ===== */}
      <nav className={`lp-navbar ${navScrolled ? 'lp-navbar-scrolled' : ''}`}>
        <div className="lp-navbar-inner">
          <div className="lp-navbar-brand" onClick={() => scrollTo(heroRef)} role="button" tabIndex={0}>
            {logoUrl && <img src={logoUrl} alt={hospitalName} className="lp-navbar-logo" />}
          </div>
          <div className={`lp-navbar-links ${mobileMenuOpen ? 'lp-navbar-links-open' : ''}`}>
            <button onClick={() => scrollTo(heroRef)}>Home</button>
            <button onClick={() => scrollTo(aboutRef)}>About</button>
            <button onClick={() => scrollTo(servicesRef)}>Services</button>
            {treatments.length > 0 && <button onClick={() => scrollTo(treatmentsRef)}>Treatments</button>}
            {diagnosticsList.length > 0 && <button onClick={() => scrollTo(diagnosticsRef)}>Diagnostics</button>}
            <button onClick={() => scrollTo(doctorsRef)}>Doctors</button>
            {galleryItems.length > 0 && <button onClick={() => scrollTo(galleryRef)}>Gallery</button>}
            {testimonials.length > 0 && <button onClick={() => scrollTo(testimonialsRef)}>Testimonials</button>}
            <button onClick={() => scrollTo(contactRef)}>Contact</button>
          </div>
          <div className="lp-navbar-actions">
            <button className="lp-nav-icon-btn" onClick={toggleTheme} title={isDark ? 'Light mode' : 'Dark mode'}>
              <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-fill'}`}></i>
            </button>
            <button className="lp-btn lp-btn-outline" onClick={() => navigate('/login')}>
              Log In
            </button>
            <button className="lp-btn lp-btn-primary" onClick={() => navigate('/onboarding')}>
              Register Hospital
            </button>
            <button className="lp-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
              <i className={`bi ${mobileMenuOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileMenuOpen && <div className="lp-mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>}

      {/* ===== HERO ===== */}
      <section className="lp-hero" ref={heroRef}>
        <div className="lp-hero-overlay"></div>
        <div className="lp-hero-particles">
          <i className="bi bi-heart-pulse-fill lp-particle lp-p1"></i>
          <i className="bi bi-capsule lp-particle lp-p2"></i>
          <i className="bi bi-bandaid-fill lp-particle lp-p3"></i>
          <i className="bi bi-clipboard2-pulse lp-particle lp-p4"></i>
          <i className="bi bi-activity lp-particle lp-p5"></i>
          <i className="bi bi-lungs-fill lp-particle lp-p6"></i>
        </div>
        <div className="lp-hero-content">
          <div className="lp-hero-badge">
            <i className="bi bi-plus-circle-fill"></i>
            <span>Trusted Healthcare</span>
          </div>
          <h1 className="lp-hero-title">{hospitalName}</h1>
          <p className="lp-hero-subtitle">
            Advanced Healthcare, Personalized for You.
            <br />
            Experience world-class medical care with cutting-edge technology.
          </p>
          <div className="lp-hero-btns">
            <button className="lp-btn lp-btn-hero" onClick={() => navigate('/login')}>
              <i className="bi bi-calendar2-check me-2"></i>Book Appointment
            </button>
            {branding.ambulance_number && (
              <a href={`tel:${branding.ambulance_number.replace(/\s/g, '')}`} className="lp-btn lp-btn-hero-outline">
                <i className="bi bi-telephone-fill me-2"></i>Emergency
              </a>
            )}
          </div>
          {/* Floating stat pills */}
          <div className="lp-hero-stats">
            <div className="lp-hero-pill">
              <i className="bi bi-people-fill"></i>
              <span>{stats.doctor_count || '10'}+ Doctors</span>
            </div>
            <div className="lp-hero-pill">
              <i className="bi bi-heart-pulse-fill"></i>
              <span>{stats.patient_count || '1000'}+ Patients</span>
            </div>
            <div className="lp-hero-pill">
              <i className="bi bi-award-fill"></i>
              <span>{stats.years || '5'}+ Years</span>
            </div>
          </div>
        </div>
        {/* Heartbeat SVG */}
        <svg className="lp-heartbeat-svg" viewBox="0 0 1200 80" preserveAspectRatio="none">
          <path
            className="lp-heartbeat-path"
            d="M0,40 L300,40 L340,10 L360,70 L380,20 L400,50 L420,35 L440,40 L900,40 L940,10 L960,70 L980,20 L1000,50 L1020,35 L1040,40 L1200,40"
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="2.5"
          />
        </svg>
      </section>

      {/* ===== ABOUT ===== */}
      <section
        className={`lp-section lp-about ${visibleSections.has('about') ? 'lp-visible' : ''}`}
        id="about"
        ref={aboutRef}
      >
        <div className="lp-container">
          <div className="lp-section-header">
            <span className="lp-tag">About Us</span>
            <h2>
              Why Choose <span className="lp-highlight">{hospitalName}</span>?
            </h2>
            <p className="lp-section-desc">
              Committed to providing exceptional healthcare with state-of-the-art facilities
            </p>
          </div>
          <div className="lp-about-grid">
            <div className="lp-about-text">
              {branding.about_us ? (
                <div dangerouslySetInnerHTML={{ __html: branding.about_us }} />
              ) : (
                <p>
                  We are dedicated to delivering top-quality healthcare services with compassion and excellence. Our
                  team of experienced professionals uses the latest medical technology to ensure the best patient
                  outcomes. From preventive care to advanced treatments, we are your trusted partner in health and
                  wellness.
                </p>
              )}
            </div>
            <div className="lp-stats-grid">
              <div className="lp-stat-card">
                <div className="lp-stat-icon">
                  <i className="bi bi-person-badge-fill"></i>
                </div>
                <div className="lp-stat-num">{stats.doctor_count || '10'}+</div>
                <div className="lp-stat-label">Expert Doctors</div>
              </div>
              <div className="lp-stat-card">
                <div className="lp-stat-icon">
                  <i className="bi bi-heart-pulse-fill"></i>
                </div>
                <div className="lp-stat-num">{stats.patient_count || '1000'}+</div>
                <div className="lp-stat-label">Patients Served</div>
              </div>
              <div className="lp-stat-card">
                <div className="lp-stat-icon">
                  <i className="bi bi-award-fill"></i>
                </div>
                <div className="lp-stat-num">{stats.years || '5'}+</div>
                <div className="lp-stat-label">Years Experience</div>
              </div>
              <div className="lp-stat-card">
                <div className="lp-stat-icon">
                  <i className="bi bi-clock-fill"></i>
                </div>
                <div className="lp-stat-num">24/7</div>
                <div className="lp-stat-label">Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      {services.length > 0 && (
        <section
          className={`lp-section lp-services ${visibleSections.has('services') ? 'lp-visible' : ''}`}
          id="services"
          ref={servicesRef}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Our Services</span>
              <h2>
                Comprehensive <span className="lp-highlight">Medical Services</span>
              </h2>
              <p className="lp-section-desc">
                We offer a wide range of healthcare services to cater to all your medical needs
              </p>
            </div>
            <div className="lp-services-grid">
              {services.map((svc, i) => (
                <div key={svc.service_id || i} className="lp-service-card" style={{ animationDelay: `${i * 0.08}s` }}>
                  <div
                    className="lp-service-icon"
                    style={{ background: `rgba(${primaryRgb}, 0.1)`, color: primaryColor }}
                  >
                    <i className={`bi ${getServiceIcon(svc.service_name)}`}></i>
                  </div>
                  <h5>{svc.service_name}</h5>
                  {svc.description && (
                    <p>{svc.description.length > 80 ? svc.description.slice(0, 80) + '...' : svc.description}</p>
                  )}
                  {svc.cost && (
                    <span className="lp-service-cost">
                      <i className="bi bi-currency-rupee"></i>
                      {svc.cost}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== OUR TEAM (Leadership + Doctors) ===== */}
      {(teamMembers.length > 0 || doctors.length > 0) && (
        <section
          className={`lp-section lp-doctors ${visibleSections.has('doctors') ? 'lp-visible' : ''}`}
          id="doctors"
          ref={doctorsRef}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Our Team</span>
              <h2>
                Meet Our <span className="lp-highlight">Expert Team</span>
              </h2>
              <p className="lp-section-desc">Experienced professionals dedicated to your health and well-being</p>
            </div>

            {/* Leadership / Team Members */}
            {teamMembers.length > 0 && (
              <>
                {doctors.length > 0 && (
                  <h5 className="text-center mb-4 fw-semibold" style={{ color: primaryColor }}>
                    <i className="bi bi-star-fill me-2"></i>Leadership
                  </h5>
                )}
                <div className="lp-doctors-grid" style={{ marginBottom: doctors.length > 0 ? '3rem' : 0 }}>
                  {teamMembers.map((m, i) => {
                    const photoSrc = m.photo ? resolvePhoto(m.photo) : null;
                    return (
                      <div key={`tm-${i}`} className="lp-doctor-card" style={{ animationDelay: `${i * 0.1}s` }}>
                        <div className="lp-doctor-photo-wrap">
                          {photoSrc ? (
                            <>
                              <img
                                src={photoSrc}
                                alt={m.name}
                                className="lp-doctor-photo"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="lp-doctor-avatar" style={{ display: 'none' }}>
                                {getInitials(m.name)}
                              </div>
                            </>
                          ) : (
                            <div className="lp-doctor-avatar">{getInitials(m.name)}</div>
                          )}
                        </div>
                        <div className="lp-doctor-info">
                          <h5>{m.name}</h5>
                          <p className="lp-doctor-spec">{m.designation}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Doctors */}
            {doctors.length > 0 && (
              <>
                {teamMembers.length > 0 && (
                  <h5 className="text-center mb-4 fw-semibold" style={{ color: primaryColor }}>
                    <i className="bi bi-heart-pulse-fill me-2"></i>Our Doctors
                  </h5>
                )}
                <div className="lp-doctors-grid">
                  {doctors.map((doc, i) => {
                    const photoSrc = doc.photo ? resolveDoctorPhoto(doc.photo) : null;
                    const name = doc.full_name?.toLowerCase().startsWith('dr') ? doc.full_name : `Dr. ${doc.full_name}`;
                    return (
                      <div
                        key={i}
                        className="lp-doctor-card"
                        style={{ animationDelay: `${i * 0.1}s`, cursor: 'pointer' }}
                        onClick={() => navigate(`/doctor/${doc.user_id}`)}
                      >
                        <div className="lp-doctor-photo-wrap">
                          {photoSrc ? (
                            <>
                              <img
                                src={photoSrc}
                                alt={doc.full_name}
                                className="lp-doctor-photo"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="lp-doctor-avatar" style={{ display: 'none' }}>
                                {getInitials(doc.full_name)}
                              </div>
                            </>
                          ) : (
                            <div className="lp-doctor-avatar">{getInitials(doc.full_name)}</div>
                          )}
                        </div>
                        <div className="lp-doctor-info">
                          <h5>{name}</h5>
                          <p className="lp-doctor-spec">{doc.specialization_name || 'General Physician'}</p>
                          {doc.qualification && <p className="lp-doctor-qual">{doc.qualification}</p>}
                          <div className="lp-doctor-meta">
                            {doc.experience_years && (
                              <span>
                                <i className="bi bi-briefcase-fill"></i>
                                {doc.experience_years} yrs
                              </span>
                            )}
                            {doc.consultation_fee && (
                              <span>
                                <i className="bi bi-currency-rupee"></i>
                                {doc.consultation_fee}
                              </span>
                            )}
                          </div>
                          <span className="lp-doctor-view-link">
                            View Profile <i className="bi bi-arrow-right-short" style={{ fontSize: '1.1rem' }}></i>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ===== EMERGENCY ===== */}
      {emergencyNumbers.length > 0 && (
        <section
          className={`lp-section lp-emergency ${visibleSections.has('emergency') ? 'lp-visible' : ''}`}
          id="emergency"
          ref={emergencyRef}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag lp-tag-danger">Emergency</span>
              <h2>
                Emergency <span className="lp-highlight">Contact Numbers</span>
              </h2>
              <p className="lp-section-desc">Quick access to emergency services - available 24/7</p>
            </div>
            <div className="lp-emergency-grid">
              {emergencyNumbers.map((svc) => (
                <a
                  key={svc.key}
                  href={`tel:${branding[svc.key].replace(/\s/g, '')}`}
                  className="lp-emergency-card"
                  style={{ '--ec-color': svc.color, '--ec-bg': svc.bg }}
                >
                  <div className="lp-emergency-icon">
                    <i className={`bi ${svc.icon}`}></i>
                  </div>
                  <div className="lp-emergency-info">
                    <h6>{svc.label}</h6>
                    <span>{branding[svc.key]}</span>
                  </div>
                  <i className="bi bi-telephone-fill lp-emergency-phone"></i>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== TREATMENTS ===== */}
      {treatments.length > 0 && (
        <section
          className={`lp-section ${visibleSections.has('treatments') ? 'lp-visible' : ''}`}
          id="treatments"
          ref={treatmentsRef}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Treatments</span>
              <h2>
                Our <span className="lp-highlight">Treatments</span>
              </h2>
              <p className="lp-section-desc">Comprehensive treatment options for various health conditions</p>
            </div>
            <div className="lp-treatments-grid">
              {treatments.map((t, i) => (
                <a
                  key={t.id}
                  href={`/page/treatment/${t.slug}`}
                  className="lp-treatment-card"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {t.image && <img src={resolvePhoto(t.image)} alt={t.title} className="lp-treatment-img" />}
                  <div className="lp-treatment-info">
                    <h5>{t.title}</h5>
                    <span className="lp-treatment-link">
                      Learn more <i className="bi bi-arrow-right"></i>
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== DIAGNOSTICS ===== */}
      {diagnosticsList.length > 0 && (
        <section
          className={`lp-section ${visibleSections.has('diagnostics') ? 'lp-visible' : ''}`}
          id="diagnostics"
          ref={diagnosticsRef}
          style={{ background: 'var(--bg-secondary)' }}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Diagnostics</span>
              <h2>
                Diagnostic <span className="lp-highlight">Services</span>
              </h2>
              <p className="lp-section-desc">State-of-the-art diagnostic facilities for accurate results</p>
            </div>
            <div className="lp-treatments-grid">
              {diagnosticsList.map((d, i) => (
                <a
                  key={d.id}
                  href={`/page/diagnostic/${d.slug}`}
                  className="lp-treatment-card"
                  style={{ animationDelay: `${i * 0.08}s` }}
                >
                  {d.image && <img src={resolvePhoto(d.image)} alt={d.title} className="lp-treatment-img" />}
                  <div className="lp-treatment-info">
                    <h5>{d.title}</h5>
                    <span className="lp-treatment-link">
                      Learn more <i className="bi bi-arrow-right"></i>
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== GALLERY ===== */}
      {galleryItems.length > 0 && (
        <section
          className={`lp-section ${visibleSections.has('gallery') ? 'lp-visible' : ''}`}
          id="gallery"
          ref={galleryRef}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Gallery</span>
              <h2>
                Our <span className="lp-highlight">Gallery</span>
              </h2>
              <p className="lp-section-desc">A glimpse of our facilities and infrastructure</p>
            </div>
            <div className="lp-gallery-grid">
              {galleryItems.map((g, i) => (
                <div key={g.id} className="lp-gallery-item" style={{ animationDelay: `${i * 0.06}s` }}>
                  <img src={resolvePhoto(g.image)} alt={g.caption || 'Gallery'} />
                  {g.caption && <div className="lp-gallery-caption">{g.caption}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== TESTIMONIALS ===== */}
      {testimonials.length > 0 && (
        <section
          className={`lp-section ${visibleSections.has('testimonials') ? 'lp-visible' : ''}`}
          id="testimonials"
          ref={testimonialsRef}
          style={{ background: 'var(--bg-secondary)' }}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Testimonials</span>
              <h2>
                What Our <span className="lp-highlight">Patients Say</span>
              </h2>
              <p className="lp-section-desc">Real experiences from our valued patients</p>
            </div>
            <div className="lp-testimonials-grid">
              {testimonials.map((t, i) => (
                <div key={i} className="lp-testimonial-card" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="lp-testimonial-header">
                    {t.patient_photo ? (
                      <img src={resolvePhoto(t.patient_photo)} alt={t.patient_name} className="lp-testimonial-photo" />
                    ) : (
                      <div
                        className="lp-testimonial-avatar"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, rgba(${primaryRgb}, 0.7))` }}
                      >
                        {getInitials(t.patient_name)}
                      </div>
                    )}
                    <div>
                      <h6>{t.patient_name}</h6>
                      <div className="lp-testimonial-stars">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <i key={s} className={`bi ${s <= (t.rating || 5) ? 'bi-star-fill' : 'bi-star'}`}></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="lp-testimonial-comment">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== CONTACT ===== */}
      {(branding.address || branding.phone_number || branding.email) && (
        <section
          className={`lp-section lp-contact ${visibleSections.has('contact') ? 'lp-visible' : ''}`}
          id="contact"
          ref={contactRef}
        >
          <div className="lp-container">
            <div className="lp-section-header">
              <span className="lp-tag">Get In Touch</span>
              <h2>
                Contact <span className="lp-highlight">Us</span>
              </h2>
            </div>
            <div className="lp-contact-grid">
              <div className="lp-contact-info">
                {branding.address && (
                  <div className="lp-contact-item">
                    <div className="lp-contact-icon">
                      <i className="bi bi-geo-alt-fill"></i>
                    </div>
                    <div>
                      <h6>Address</h6>
                      <p>{branding.address}</p>
                    </div>
                  </div>
                )}
                {branding.phone_number && (
                  <a href={`tel:${branding.phone_number}`} className="lp-contact-item">
                    <div className="lp-contact-icon">
                      <i className="bi bi-telephone-fill"></i>
                    </div>
                    <div>
                      <h6>Phone</h6>
                      <p>{branding.phone_number}</p>
                    </div>
                  </a>
                )}
                {branding.email && (
                  <a href={`mailto:${branding.email}`} className="lp-contact-item">
                    <div className="lp-contact-icon">
                      <i className="bi bi-envelope-fill"></i>
                    </div>
                    <div>
                      <h6>Email</h6>
                      <p>{branding.email}</p>
                    </div>
                  </a>
                )}
                {branding.whatsapp_number && (
                  <a
                    href={`https://wa.me/${branding.whatsapp_number.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lp-contact-item"
                  >
                    <div className="lp-contact-icon" style={{ background: 'rgba(37,211,102,0.1)', color: '#25d366' }}>
                      <i className="bi bi-whatsapp"></i>
                    </div>
                    <div>
                      <h6>WhatsApp</h6>
                      <p>{branding.whatsapp_number}</p>
                    </div>
                  </a>
                )}
                {/* Social Links */}
                {(branding.facebook_url || branding.instagram_url || branding.twitter_url || branding.youtube_url) && (
                  <div className="lp-social-links">
                    {branding.facebook_url && (
                      <a
                        href={branding.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-social-icon"
                      >
                        <i className="bi bi-facebook"></i>
                      </a>
                    )}
                    {branding.instagram_url && (
                      <a
                        href={branding.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-social-icon"
                      >
                        <i className="bi bi-instagram"></i>
                      </a>
                    )}
                    {branding.twitter_url && (
                      <a
                        href={branding.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-social-icon"
                      >
                        <i className="bi bi-twitter-x"></i>
                      </a>
                    )}
                    {branding.youtube_url && (
                      <a
                        href={branding.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-social-icon"
                      >
                        <i className="bi bi-youtube"></i>
                      </a>
                    )}
                  </div>
                )}
              </div>
              <div className="lp-contact-map">
                {(() => {
                  const embedSrc = extractMapSrc(branding.google_map_location);
                  const externalUrl = branding.google_map_location;
                  if (embedSrc) {
                    return (
                      <>
                        <iframe
                          src={embedSrc}
                          className="lp-map-iframe"
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Location"
                        ></iframe>
                        <a
                          href={externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="lp-map-external-link"
                        >
                          <i className="bi bi-box-arrow-up-right me-1"></i>Open in Google Maps
                        </a>
                      </>
                    );
                  }
                  if (externalUrl) {
                    return (
                      <>
                        <div className="lp-map-placeholder">
                          <i className="bi bi-geo-alt-fill"></i>
                          <p>View our location on Google Maps</p>
                        </div>
                        <a
                          href={externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="lp-map-external-link"
                        >
                          <i className="bi bi-box-arrow-up-right me-1"></i>Open in Google Maps
                        </a>
                      </>
                    );
                  }
                  return (
                    <div className="lp-map-placeholder">
                      <i className="bi bi-geo-alt-fill"></i>
                      <p>Visit us at our hospital</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== FOOTER ===== */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <div className="lp-footer-brand-row">
                {logoUrl && <img src={logoUrl} alt={hospitalName} className="lp-footer-logo" />}
                <h3>{hospitalName}</h3>
              </div>
              <p>Quality healthcare management powered by technology</p>
            </div>
            <div className="lp-footer-col">
              <h4>Quick Links</h4>
              <button onClick={() => scrollTo(aboutRef)}>About Us</button>
              <button onClick={() => scrollTo(servicesRef)}>Services</button>
              {treatments.length > 0 && <button onClick={() => scrollTo(treatmentsRef)}>Treatments</button>}
              {diagnosticsList.length > 0 && <button onClick={() => scrollTo(diagnosticsRef)}>Diagnostics</button>}
              <button onClick={() => scrollTo(doctorsRef)}>Doctors</button>
              {galleryItems.length > 0 && <button onClick={() => scrollTo(galleryRef)}>Gallery</button>}
              {testimonials.length > 0 && <button onClick={() => scrollTo(testimonialsRef)}>Testimonials</button>}
              <button onClick={() => scrollTo(contactRef)}>Contact</button>
            </div>
            {/* Legal / Policy links — only if content exists */}
            {(branding.privacy_policy ||
              branding.terms_conditions ||
              branding.refund_policy ||
              branding.cancellation_policy ||
              branding.mission ||
              branding.vision) && (
              <div className="lp-footer-col">
                <h4>Legal</h4>
                {branding.privacy_policy && (
                  <button onClick={() => navigate('/page/privacy-policy')}>Privacy Policy</button>
                )}
                {branding.terms_conditions && (
                  <button onClick={() => navigate('/page/terms-and-conditions')}>Terms & Conditions</button>
                )}
                {branding.refund_policy && (
                  <button onClick={() => navigate('/page/refund-policy')}>Refund Policy</button>
                )}
                {branding.cancellation_policy && (
                  <button onClick={() => navigate('/page/cancellation-policy')}>Cancellation Policy</button>
                )}
                {branding.mission && <button onClick={() => navigate('/page/mission')}>Our Mission</button>}
                {branding.vision && <button onClick={() => navigate('/page/vision')}>Our Vision</button>}
              </div>
            )}
            <div className="lp-footer-col">
              <h4>Contact</h4>
              {branding.phone_number && (
                <p>
                  <i className="bi bi-telephone me-2"></i>
                  {branding.phone_number}
                </p>
              )}
              {branding.email && (
                <p>
                  <i className="bi bi-envelope me-2"></i>
                  {branding.email}
                </p>
              )}
              {branding.address && (
                <p>
                  <i className="bi bi-geo-alt me-2"></i>
                  {branding.address}
                </p>
              )}
            </div>
          </div>
          <div className="lp-footer-bottom">
            <p>
              &copy; {new Date().getFullYear()} {hospitalName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* ===== FLOATING ACTION BUTTON (Speed Dial) ===== */}
      {(branding.phone_number || branding.whatsapp_number) && (
        <div className="lp-fab-wrap">
          {branding.phone_number && (
            <a
              href={`tel:${branding.phone_number.replace(/\s/g, '')}`}
              className="lp-fab-action lp-fab-call"
              title="Call us"
            >
              <i className="bi bi-telephone-fill"></i>
              <span>Call us</span>
            </a>
          )}
          {branding.whatsapp_number && (
            <a
              href={`https://wa.me/${branding.whatsapp_number.replace(/[^0-9]/g, '')}`}
              className="lp-fab-action lp-fab-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              title="WhatsApp"
            >
              <i className="bi bi-whatsapp"></i>
              <span>WhatsApp</span>
            </a>
          )}
          <button
            className="lp-fab-action lp-fab-share"
            title="Share"
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: hospitalName, url: window.location.href }).catch(() => {});
              } else {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied!');
              }
            }}
          >
            <i className="bi bi-share-fill"></i>
            <span>Share</span>
          </button>
          <button className="lp-fab-toggle">
            <i className="bi bi-plus-lg lp-fab-icon-open"></i>
            <i className="bi bi-x-lg lp-fab-icon-close"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
